import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export function Field({ label, hint, error, children, htmlFor, className }: {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && <label htmlFor={htmlFor} className="text-[11px] font-semibold uppercase tracking-wider text-muted">{label}</label>}
      {children}
      {error ? <p className="text-xs text-danger">{error}</p> : hint ? <p className="text-xs text-faint">{hint}</p> : null}
    </div>
  );
}

export const inputClass = cn(
  'h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm text-ink shadow-[inset_0_1px_2px_rgb(0_0_0/0.03)]',
  'placeholder:text-faint transition-colors hover:border-line-strong',
  'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25 disabled:opacity-60',
);

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leading?: ReactNode;
}

export function Input({ label, hint, error, className, id, leading, ...rest }: InputProps) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      <div className="relative">
        {leading && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint">{leading}</span>}
        <input id={inputId} className={cn(inputClass, leading ? 'pl-9' : undefined, error && 'border-danger', className)} {...rest} />
      </div>
    </Field>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  options: Array<{ value: string; label: string }>;
}

export function Select({ label, hint, options, className, id, ...rest }: SelectProps) {
  const auto = useId();
  const selectId = id ?? auto;
  return (
    <Field label={label} hint={hint} htmlFor={selectId}>
      <div className="relative">
        <select id={selectId} className={cn(inputClass, 'appearance-none pr-9', className)} {...rest}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-faint" />
      </div>
    </Field>
  );
}

/** A compact filter select for toolbars — no label, a value that reads as a chip. */
export function FilterSelect({ value, onChange, options, ariaLabel }: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  ariaLabel: string;
}) {
  const active = value !== '' && value !== options[0]?.value;
  return (
    <div className="relative">
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-9 max-w-[220px] appearance-none truncate rounded-lg border pl-2.5 pr-7 text-xs font-semibold transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          active ? 'border-accent/40 bg-accent-soft text-accent-ink' : 'border-line bg-surface text-muted hover:border-line-strong hover:text-ink',
        )}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-faint" />
    </div>
  );
}
